import React, { useState, useReducer, useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Row, Col, Form, Container } from "react-bootstrap";
import { validatepaypalForm } from "../validationFunctions/inputValidations.tsx";
import { FormattedMessage, useIntl } from "react-intl";
import {
  FormErrorAction,
  PaypalFormErrorState,
} from "types/validationError.types.ts";
import { v4 as uuidv4 } from 'uuid';

import paymentOptions from '../mockdata/paymentOptions.json';
import { UUIDContext, PBLResponseContext } from "../UUIDContexted.tsx";
import { submitPaypalPaymentRequest } from "../api/apiService.ts";
import CONSTANTS from "../utils/constants.js";
import { PaymentProcessingLoader } from './PaymentProcessingLoader.tsx';
import PaylaDcs from './PaylaDsc.tsx';
import PaymentNotSupported from "./PaymentNotSupported.tsx";

//custom error message component
const FormFieldError: React.FC<{
  id: string;
  value: string;
  testId: string;
}> = ({ id, value, testId }) =>
    value !== "typing" &&
    value !== "" && (
      <span
        id={id}
        data-testid={testId}
        className="error_message"
      >
        <FormattedMessage id={value} />
      </span>
    );

const PaypalPage: React.FC = () => {
  const PBLData = useContext(UUIDContext);
  const { setPblResponseData } = useContext(PBLResponseContext);
  const { uuid } = useParams<{ uuid: string }>();
  const [isPaymentMethodAvailable, setIsPaymentMethodAvailable] = useState(false);
  const [submissionAttempted, setSubmissionAttempted] = useState(false);
  const [showPaymentProcessing, setShowPaymentProcessing] = useState(false);
  const [snippetToken, setSnippetToken] = useState("");
  const intl = useIntl();

  const [formData, setFormData] = useState({
    paymentMethod: "840",
    linkId: PBLData?.linkId,
    agreed: false,
  });

  // Fires when back/forward button is clicked
  window.addEventListener('popstate', (event) => {
    console.log('Navigation intercepted', event.state);
    setShowPaymentProcessing(false)
    // Can react to or potentially block navigation
  });

  //generate snippetToken only once
  useEffect(() => {
    const partnerMerchantId = PBLData ? PBLData.bnplId : '';
    setSnippetToken(`${CONSTANTS.PAYLA_PARTNER_ID}_${partnerMerchantId}_${uuidv4()}`);
    setPblResponseData(null);
  }, []);

  useEffect(() => {
    if (PBLData && PBLData.paymentMethods && PBLData.paymentMethods.length > 0) {
      let paypalPaymentMethodsID = paymentOptions.find(option => option.id === "paypal")?.paymentMethodsID;
      if (paypalPaymentMethodsID) {
        let isPaymentMethodAvailable = PBLData.paymentMethods.includes(paypalPaymentMethodsID);
        setIsPaymentMethodAvailable(isPaymentMethodAvailable)
      }
    }
  }, [PBLData]);

  //this function recurses through formErrorState and search for non-empty strings (errors) and returns true if errors not present
  const containsError = (errors: PaypalFormErrorState): boolean => {
    const check = (obj: any): boolean => {
      for (const key in obj) {
        if (typeof obj[key] === "object") {
          if (check(obj[key])) {
            return true;
          }
        } else if (typeof obj[key] === "string" && obj[key] !== "") {
          return true;
        }
      }
      return false;
    };
    return check(errors);
  };

  const initialErrorState: PaypalFormErrorState = {
    notAgreedError: ""
  };

  const formErrorReducer = (
    errorState: PaypalFormErrorState,
    action: FormErrorAction
  ): PaypalFormErrorState => {
    switch (action.type) {
      case "SET_NOT_AGREED_ERROR":
        return {
          ...errorState,
          notAgreedError: action.value,
        };
      default:
        return errorState;
    }
  };

  const [formErrorState, errorDispatch] = useReducer(
    formErrorReducer,
    initialErrorState
  );

  const handleSubmit = async () => {
    validatepaypalForm(
      formData,
      errorDispatch
    );
    setSubmissionAttempted(true);
  }

  useEffect(() => {
    const makePostRequest = async () => {
      if (submissionAttempted) {
        const hasError = containsError(formErrorState);

        if (!hasError) {
          setShowPaymentProcessing(true);
          setPblResponseData(null);
          // code for the post request
          try {
            const response = await submitPaypalPaymentRequest(formData, uuid ? uuid : '');
            // Check if actionType is REDIRECT
            if (response.createPaymentResponse?.merchantAction?.actionType === 'REDIRECT') {
              const redirectURL = response.createPaymentResponse.merchantAction.redirectData?.redirectURL;
              if (redirectURL && /^https?:\/\//.test(redirectURL)) {
                // Redirect to PayPal 
                window.location.href = redirectURL;
              } else {
                setPblResponseData({ error: 'CUSTOM_ERROR_PAYPAL', linkId: uuid });
                setShowPaymentProcessing(false);
              }
            }
          } catch (error: any) {
            setPblResponseData({ error: error, linkId: uuid });
            setShowPaymentProcessing(false);
          }
        }
      }
    }
    makePostRequest();

    return () => {
      setSubmissionAttempted(false);
    }
  }, [submissionAttempted]);


  return (
    <>
      {isPaymentMethodAvailable ?
        <Container fluid className="d-flex flex-column g-0" data-testid="paypal_payment_section">
          <Row className="d-flex justify-content-center align-items-end mb-24 mt-2">
            <h5 data-testid="pm-label-terms-payment" className="section-title">
              {intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.CHECKBOX_SEC_TITLE, defaultMessage: "Terms of Payment" })}
            </h5>
            <div className="terms-checkbox mt-3" data-testid="pm-terms-checkbox">
              <Form.Check.Input
                type="checkbox"
                className="terms-checkbox-input m-0"
                checked={formData.agreed}
                onChange={(e) => setFormData((prevState) => ({
                  ...prevState,
                  agreed: e.target.checked,
                }))}
                data-testid="pm-terms-checkbox-input"
              />
              <label className="terms-checkbox-label" data-testid="pm-terms-checkbox-label">
                <FormattedMessage
                  id={CONSTANTS.PAYMENT_FORM.LABEL.CHECKBOX_TEXT}
                  defaultMessage={CONSTANTS.PAYMENT_CHECKBOX_TEXT}
                />
              </label>
            </div>
            {formData.agreed ? (
              (formErrorState.notAgreedError = "")
            ) : (
              <FormFieldError
                id="not-agree"
                value={formErrorState.notAgreedError}
                testId="pn-not-agree"
              />
            )}
            <PaylaDcs snippetToken={snippetToken} agreed={formData.agreed} />
            <div className="term_policy_text mt-3"
              dangerouslySetInnerHTML={{
                __html: intl.formatMessage({
                  id: CONSTANTS.PAYMENT_FORM.LABEL.CHECKBOX_DESCRIPTION_PAYPAL,
                  defaultMessage: CONSTANTS.CHECKBOX_DESCRIPTION_PAYPAL,
                }),
              }}
            ></div>
          </Row>

          <Row className="d-flex align-items-center">
            {showPaymentProcessing ? (
              <Col>
                <PaymentProcessingLoader />
              </Col>
            ) : (
              <Col className="text-end">
                <button
                  className="btn btn-primary text-white px-4 rounded-4 volte-medium"
                  onClick={handleSubmit}
                  data-testid="pm-submit-btn"
                >
                  <FormattedMessage
                    id={CONSTANTS.PAYMENT_FORM.BUTTON.SUBMIT_PAYMENT}
                    defaultMessage="Complete payment"
                  />
                </button>
              </Col>
            )}
          </Row>
        </Container>
        : <PaymentNotSupported />
      }
    </>
  );
};

export default React.memo(PaypalPage);
